import { useState, useEffect, useCallback } from 'react';
import {
  Search, Plus, X, Loader2, Shield, Users, Mail,
  CheckCircle, AlertCircle, SlidersHorizontal,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { usuariosService } from '@/services/usuarios.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';

const roleColors: Record<string, string> = {
  SUPER_ADMIN: 'bg-purple-100 text-purple-700',
  ADMIN: 'bg-blue-100 text-blue-700',
  GERENTE: 'bg-green-100 text-green-700',
  CAJERO: 'bg-amber-100 text-amber-700',
  VENDEDOR: 'bg-teal-100 text-teal-700',
  ALMACENISTA: 'bg-orange-100 text-orange-700',
  CONTADOR: 'bg-rose-100 text-rose-700',
};

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  GERENTE: 'Gerente',
  CAJERO: 'Cajero',
  VENDEDOR: 'Vendedor',
  ALMACENISTA: 'Almacenista',
  CONTADOR: 'Contador',
};

interface UserFormData {
  username: string;
  email: string;
  password: string;
  fullName: string;
  roleId: string;
  branchId: string;
}

const emptyForm: UserFormData = {
  username: '', email: '', password: '', fullName: '', roleId: '', branchId: '',
};

export function UsersPage() {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { error, errorType, handleApiError: onApiError, handleAuthRedirect } = useApiError();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [form, setForm] = useState<UserFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const data = await usuariosService.findAll(user.company.id);
      setUsers(data || []);
    } catch (err: any) { onApiError(err); }
    setLoading(false);
  }, [user]);

  const loadRoles = useCallback(async () => {
    if (!user?.company?.id) return;
    try {
      const data = await usuariosService.getRoles(user.company.id);
      setRoles(data || []);
    } catch {} // eslint-disable-line no-empty
  }, [user]);

  const loadBranches = useCallback(async () => {
    if (!user?.company?.id) return;
    try {
      const data = await usuariosService.getBranches(user.company.id);
      setBranches(data || []);
    } catch {} // eslint-disable-line no-empty
  }, [user]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadRoles(); loadBranches(); }, [loadRoles, loadBranches]);

  const openCreate = async () => {
    setEditingUser(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = async (u: any) => {
    try {
      const full = await usuariosService.findById(u.id);
      setEditingUser(full);
      setForm({
        username: full.username || '',
        email: full.email || '',
        password: '',
        fullName: full.fullName || '',
        roleId: full.role?.id || '',
        branchId: full.branch?.id || '',
      });
      setModalOpen(true);
    } catch (err: any) { onApiError(err); }
  };

  const handleSubmit = async () => {
    if (!user?.company?.id || !form.fullName.trim() || !form.username.trim() || !form.email.trim() || !form.roleId) return;
    if (!editingUser && !form.password.trim()) return; // Password required for new users

    setSaving(true);
    try {
      if (editingUser) {
        const payload: any = {
          fullName: form.fullName,
          username: form.username,
          email: form.email,
          roleId: form.roleId,
        };
        if (form.branchId) payload.branchId = form.branchId;
        else payload.branchId = null;
        if (form.password.trim()) payload.password = form.password;
        await usuariosService.update(editingUser.id, payload);
      } else {
        await usuariosService.create({
          companyId: user.company.id,
          fullName: form.fullName,
          username: form.username,
          email: form.email,
          password: form.password,
          roleId: form.roleId,
          branchId: form.branchId || undefined,
        });
      }
      setModalOpen(false);
      loadUsers();
    } catch (err: any) { onApiError(err); }
    setSaving(false);
  };

  const handleDeactivate = async (u: any) => {
    if (u.id === user?.id) {
      await dialogs.alert('No puedes desactivarte a ti mismo');
      return;
    }
    if (!await dialogs.confirm(`¿Desactivar usuario "${u.fullName}" (${u.username})?`)) return;
    try {
      await usuariosService.deactivate(u.id);
      loadUsers();
    } catch (err: any) { onApiError(err); }
  };

  const filtered = users.filter((u: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      u.fullName?.toLowerCase().includes(q) ||
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadUsers} onLogin={handleAuthRedirect} />
      {!error && (
      <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Usuarios</h1>
          <p className="text-sm text-ios-gray mt-1">{users.length} usuarios registrados</p>
        </div>
        <button onClick={openCreate} className="ios-button flex items-center gap-2 px-4 py-2">
          <Plus size={16} />
          Nuevo Usuario
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre, usuario, email, rol..."
          className="ios-input w-full pl-9 h-9 text-sm"
        />
      </div>

      {/* Users table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-ios-gray" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Users size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">
            {search ? 'Sin resultados' : 'No hay usuarios'}
          </h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'Intenta con otros términos' : 'Crea tu primer usuario'}
          </p>
          {!search && (
            <button onClick={openCreate} className="ios-button">
              + Nuevo Usuario
            </button>
          )}
        </div>
      ) : (
        <div className="ios-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Usuario</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Rol</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Sucursal</th>
                  <th className="text-center px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Estado</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {filtered.map((u: any) => (
                  <tr key={u.id} className="hover:bg-ios-gray-6/30 transition-colors">
                    <td className="px-4 py-3" onClick={() => openEdit(u)}>
                      <div className="flex items-center gap-3 cursor-pointer">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          !u.isActive ? 'bg-gray-100 text-gray-400' :
                          u.role?.name === 'SUPER_ADMIN' ? 'bg-purple-100 text-purple-600' :
                          'bg-ios-blue/10 text-ios-blue'
                        }`}>
                          {u.fullName?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className={`font-medium ${!u.isActive ? 'text-gray-400' : ''}`}>{u.fullName}</p>
                          <p className="text-[11px] text-ios-gray">@{u.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ios-gray">
                      {u.email ? (
                        <span className="flex items-center gap-1.5 text-xs">
                          <Mail size={12} className="flex-shrink-0" />
                          {u.email}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        roleColors[u.role?.name] || 'bg-gray-100 text-gray-600'
                      }`}>
                        <Shield size={10} className="mr-1" />
                        {roleLabels[u.role?.name] || u.role?.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-ios-gray">
                      {u.branch?.name || (
                        <span className="italic opacity-60">Sin sucursal</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.isActive ? (
                        <span className="inline-flex items-center gap-1 text-ios-green text-[10px] font-medium">
                          <CheckCircle size={14} /> Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-400 text-[10px] font-medium">
                          <AlertCircle size={14} /> Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(u)}
                          className="p-1.5 rounded-lg hover:bg-ios-blue/5 text-ios-gray hover:text-ios-blue transition-colors"
                          title="Editar"
                        >
                          <SlidersHorizontal size={14} />
                        </button>
                        <button
                          onClick={() => handleDeactivate(u)}
                          disabled={u.id === user?.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            !u.isActive ? 'text-gray-300 cursor-not-allowed' :
                            u.id === user?.id ? 'text-gray-300 cursor-not-allowed' :
                            'text-ios-gray hover:text-ios-red hover:bg-red-50'
                          }`}
                          title={u.id === user?.id ? 'No puedes desactivarte' : 'Desactivar'}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5 sticky top-0 bg-white z-10">
              <h3 className="font-semibold">
                {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nombre completo *</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm(p => ({ ...p, fullName: e.target.value }))}
                  placeholder="Juan Pérez"
                  className="ios-input w-full"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">Usuario *</label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))}
                    placeholder="jperez"
                    className="ios-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="juan@empresa.com"
                    className="ios-input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">
                  Contraseña {editingUser ? '(dejar vacío para mantener)' : '*'}
                </label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder={editingUser ? '•••••• (dejar vacío)' : 'Mín. 6 caracteres'}
                  className="ios-input w-full"
                  minLength={editingUser ? 0 : 6}
                />
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Rol *</label>
                <select
                  value={form.roleId}
                  onChange={(e) => setForm(p => ({ ...p, roleId: e.target.value }))}
                  className="ios-input w-full"
                >
                  <option value="">Seleccionar rol...</option>
                  {roles.map((r: any) => (
                    <option key={r.id} value={r.id}>{r.name} - {r.description || ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-ios-gray mb-1 block">Sucursal</label>
                <select
                  value={form.branchId}
                  onChange={(e) => setForm(p => ({ ...p, branchId: e.target.value }))}
                  className="ios-input w-full"
                >
                  <option value="">Sin sucursal</option>
                  {branches.map((b: any) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-ios-gray mt-1">Opcional. Si no se asigna, el usuario podrá acceder desde cualquier sucursal</p>
              </div>
            </div>

            <div className="p-4 border-t border-ios-gray-5 flex gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !form.fullName.trim() || !form.username.trim() || !form.email.trim() || !form.roleId || (!editingUser && !form.password.trim())}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Guardando...' : editingUser ? 'Actualizar' : 'Crear Usuario'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
